import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import '../../../styles/Login.css';
import ProductsListing from '@src/components/ProductListing/ProductsListing';
import { CommentIcon } from '@src/components/Icons';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { error } from 'console';
import { useNavigate, useParams } from 'react-router-dom';
import { LocalStorage } from '@src/storage/LocalStorage';
import moment from 'moment';
import { PDFDocument, rgb } from 'pdf-lib';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	padding: 30px;
	height: 100vh;
	overflow: scroll;
	@media screen and (max-width: 600px) {
		padding: 10px;
		overflow: scroll;
		height: calc(100vh - 68px);
		margin-bottom: 68px;
	}
	#orderContainer {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 568px;
		gap: 15px;
		@media screen and (max-width: 600px) {
			width: 328px;
			align-items: flex-start;
		}

		#title {
			color: var(--color-primary);
			font-family: Poppins;
			font-size: 32px;
			font-weight: 600;
			letter-spacing: -0.32px;
			@media screen and (max-width: 600px) {
				font-size: 20px;
				letter-spacing: -0.2px;
			}
		}
	}
`;

const OrderInfo = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	justify-content: space-between;
	#orderNo {
		display: flex;
		flex-direction: column;
		gap: 8px;
		div {
			display: flex;
			align-items: center;
			gap: 8px;
			#orderId {
				color: var(--default-text-color);
				font-family: Poppins;
				font-size: 14px;
				font-weight: 600;
			}
		}
	}
	#orderDates {
		display: flex;
		flex-direction: column;
		gap: 8px;
		div {
			display: flex;
			gap: 8px;
		}
	}
	@media screen and (max-width: 600px) {
		flex-direction: column;
		gap: 8px;
		align-items: flex-start;
	}
`;

const AmountContainer = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;
	border-radius: 4px;
	background: #fff2cc;
	height: auto;
	padding: 12px;
	gap: 10px;
	h3 {
		color: var(--default-text-color);
		font-size: 16px;
		font-weight: 700;
	}
	div {
		display: flex;
		align-items: center;
		gap: 10px;
		p {
			color: #333;
			font-size: 14px;
			font-weight: 600;
		}
	}
`;

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 68px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 100;
	position: fixed;
	bottom: 0;
`;

const paraStyling = {
	color: 'var(--lighter-text-color)',
	fontSize: '12px',
	fontWeight: 600,
};

const spanStyling = {
	color: '#333',
	fontSize: '12px',
	fontWeight: 600,
};

interface Order {
	vendor_id: number;
	customer_id: number;
	cheeni_order_id: number;
	order_time_id: string;
	requestedDeliveryDate: string;
	order_id: number;
	firstname: string;
	lastname: string;
	email: string;
	telephone: string;
	comment: string;
	total: string;
	date_added: string;
	order_status_id: number;
	status: {
		order_status_id: number;
		name: string;
	};
	products: Product[];
}

interface Product {
	name: string;
	quantity: number;
	price: string;
	total: string;
	product_id: number;
}

const OrderDetails = () => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const { instanceId } = useParams();
	const navigate = useNavigate();
	const [orderDetails, setOrderDetails] = useState<Order | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});
	// const products = [
	// 	{ quantity: 10, name: 'Widget A', unit: 'piece', price: 5.99 },
	// 	{ quantity: 5, name: 'Widget B', unit: 'set', price: 12.49 },
	// 	{ quantity: 20, name: 'Widget C', unit: 'box', price: 8.99 },
	// ];

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const displayError = (msg: string) => {
		setErrorMessage(msg);
		setErrorVisible(true);
	};

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const exportToPDF = async () => {
		try {
			if (orderDetails) {
				const {
					order_time_id,
					firstname,
					date_added,
					requestedDeliveryDate,
					products,
					total,
					comment,
				} = orderDetails;

				const pdfDoc = await PDFDocument.create();
				const pageWidth = 600;
				const pageHeight = 800; // Adjusted height to accommodate multiple pages
				let currentPage = pdfDoc.addPage([pageWidth, pageHeight]);
				let currentPageY = pageHeight - 50; // Start drawing content from the top of the page

				// Function to check if current content exceeds the current page, if yes, add a new page
				const checkPageOverflow = (heightNeeded) => {
					if (currentPageY - heightNeeded < 50) {
						// 50 is the bottom margin
						currentPage = pdfDoc.addPage([pageWidth, pageHeight]);
						currentPageY = pageHeight - 50; // Reset current Y position for new page
					}
				};

				currentPage.drawText('Order Details', {
					x: 50,
					y: currentPageY,
					size: 20,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 30; // Move Y position up to make space for the next line

				currentPage.drawText(`Order ID: ${order_time_id}`, {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;

				currentPage.drawText(`Dealer Name: ${firstname}`, {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;

				currentPage.drawText(`Order Date: ${moment(date_added).format('DD-MMM-YYYY hh:mmA')}`, {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;

				currentPage.drawText(
					`Delivery Date: ${moment(requestedDeliveryDate).format('DD-MMM-YYYY')}`,
					{
						x: 50,
						y: currentPageY,
						size: 12,
						color: rgb(0, 0, 0),
					}
				);
				currentPageY -= 20;

				currentPageY -= 20; // Add space before Products section

				// Draw divider line
				currentPage.drawLine({
					start: { x: 50, y: currentPageY },
					end: { x: pageWidth - 50, y: currentPageY },
					thickness: 1,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;
				// Draw Products
				checkPageOverflow((products.length + 1) * 20); // Space for Products title and each product
				currentPage.drawText('Products:', {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;
				products.forEach((product, index) => {
					checkPageOverflow(20); // Check if there's space for each product
					currentPage.drawText(`${product.name} - ${product.quantity} x ${product.price} INR`, {
						x: 50,
						y: currentPageY,
						size: 12,
						color: rgb(0, 0, 0),
					});
					currentPageY -= 20;
				});

				// Add space before Total and Comments section
				currentPageY -= 20;

				// Draw divider line
				currentPage.drawLine({
					start: { x: 50, y: currentPageY },
					end: { x: pageWidth - 50, y: currentPageY },
					thickness: 1,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20; // Adjust Y position after drawing the line

				// Draw Total
				checkPageOverflow(40); // Space for Total and Comments
				currentPage.drawText(`Total: ${total} INR`, {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});
				currentPageY -= 20;

				// Draw Comments
				currentPage.drawText(`Comments: ${comment}`, {
					x: 50,
					y: currentPageY,
					size: 12,
					color: rgb(0, 0, 0),
				});

				const pdfBytes = await pdfDoc?.save();

				// Convert bytes to a Blob
				const blob = new Blob([pdfBytes], { type: 'application/pdf' });

				const a = document.createElement('a');
				a.href = URL.createObjectURL(blob);
				a.download = `order_${order_time_id}.pdf`;
				a.click();
			}
		} catch (err) {
			displayError('Something went wrong!');
			console.log(err);
		}
	};

	const getOrder = () => {
		const localStorage = new LocalStorage();
		const publicToken = localStorage.getItem('public-token');
		apiService.getOrdersDetailsPublicPage(
			(response: AxiosResponse) => {
				setOrderDetails(response?.data?.data);
				setIsLoading(false);
			},
			(error: AxiosError) => {
				if (error.response && [404, 401].includes(error.response.status)) {
					console.log('Error 404: Resource not found');
					navigate(`/public/order/${instanceId}`);
				} else {
					console.log('Error', error);
					displayError('Something went wrong!');
				}
				setIsLoading(false);
			},
			instanceId,
			publicToken
		);
	};

	useEffect(() => {
		getOrder();
	}, []);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	if (isLoading) {
		return (
			<Box
				sx={{
					width: '100%',
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<div className="chat__body__loadingMore__wrapper">
					<CircularProgress size={28} />
				</div>
			</Box>
		);
	}

	return (
		<Container className="login-container">
			<div id="orderContainer">
				<p id="title">Review Your Order!</p>
				<OrderInfo>
					<div id="orderNo">
						<div>
							<p style={paraStyling}>Order No:</p>
							<span id="orderId">#{orderDetails?.order_time_id}</span>
						</div>
						<div>
							<p style={paraStyling}>Restaurant:</p>
							<span style={spanStyling}>{orderDetails?.firstname}</span>
						</div>
					</div>
					<div id="orderDates">
						<div>
							<p style={paraStyling}>Ordered Date:</p>
							<span style={spanStyling}>
								{moment(orderDetails?.date_added).format('DD-MMM-YYYY hh:mmA')}
							</span>
						</div>
						<div>
							<p style={paraStyling}>Request Delivery:</p>
							<span style={spanStyling}>
								{moment(orderDetails?.requestedDeliveryDate).format('DD-MMM-YYYY')}
							</span>
						</div>
					</div>
				</OrderInfo>
				<ProductsListing
					products={orderDetails?.products || []}
					style={{
						height: 'calc(100vh - 350px)',
						width: '100%',
						overflowY: 'scroll',
						border: '1.5px solid var(--gray-color-light)',
						borderRadius: '4px',
						padding: '0px',
					}}
				/>
				<AmountContainer>
					<h3>Total Amount: ₹ {orderDetails?.total}</h3>
					{orderDetails?.comment ? (
						<div style={{ whiteSpace: 'pre-line', height: 'auto' }}>
							<CommentIcon />
							<p>{orderDetails?.comment}</p>
						</div>
					) : null}
				</AmountContainer>
				{windowDimensions?.width > 600 ? (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<CustomButton
							title="Export"
							handleClick={exportToPDF}
							color="primary"
							styles={{
								width: '113px',
								height: '36px',
							}}
							disabled={false}
						/>
					</Box>
				) : null}
			</div>
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
			{windowDimensions?.width <= 600 ? (
				<StyledBottomBtnContainer>
					<CustomButton
						title="Export"
						handleClick={exportToPDF}
						color="primary"
						styles={{
							width: '328px',
							height: '36px',
						}}
						disabled={false}
					/>
				</StyledBottomBtnContainer>
			) : null}
		</Container>
	);
};

export default OrderDetails;
